import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, idr, openDocument } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, ArrowUpRight, Bot, ExternalLink, Code2, Copy, Download, Search } from "lucide-react";

export default function Quotations() {
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  useEffect(() => { api.get("/quotations").then((r) => setQuotes(r.data)).catch(() => {}); }, []);
  const filtered = quotes.filter((q) =>
    (status === "all" || q.status === status) &&
    `${q.quote_number} ${q.customer_name} ${q.customer_company || ""}`.toLowerCase().includes(search.toLowerCase()) &&
    (!dateFrom || (q.created_at || "").slice(0, 10) >= dateFrom) &&
    (!dateTo || (q.created_at || "").slice(0, 10) <= dateTo)
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const snippet = `<iframe src="${origin}/embed" width="100%" height="580" style="border:0;border-radius:8px;max-width:560px" title="Minta Penawaran CV GRcom" loading="lazy"></iframe>`;
  const copySnippet = () => { navigator.clipboard.writeText(snippet); toast.success("Kode widget disalin"); };

  return (
    <div data-testid="quotations-page">
      <PageHeader overline="Dokumen" title="Penawaran">
        <button data-testid="export-quotations-btn" onClick={() => openDocument("/export/quotations.xlsx").catch(() => toast.error("Gagal ekspor"))} className="flex items-center gap-2 border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary transition-colors">
          <Download className="w-4 h-4" /> Ekspor Excel
        </button>
        <Dialog>
          <DialogTrigger asChild>
            <button data-testid="widget-btn" className="flex items-center gap-2 border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary transition-colors">
              <Code2 className="w-4 h-4" /> Widget Website
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-heading">Widget Form Penawaran</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Tempel kode berikut di halaman grstore.id untuk menampilkan form permintaan harga. Setiap kiriman otomatis masuk ke daftar Penawaran sebagai "Permintaan".</p>
            <pre className="bg-secondary rounded-sm p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid="widget-snippet">{snippet}</pre>
            <div className="flex gap-2">
              <Button onClick={copySnippet} className="rounded-sm" data-testid="copy-widget-btn"><Copy className="w-4 h-4 mr-2" /> Salin Kode</Button>
              <a href="/embed" target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-sm"><ExternalLink className="w-4 h-4 mr-2" /> Pratinjau</Button></a>
            </div>
          </DialogContent>
        </Dialog>
        <a href="/request" target="_blank" rel="noreferrer" data-testid="public-form-link" className="flex items-center gap-2 border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary transition-colors">
          <ExternalLink className="w-4 h-4" /> Form Publik
        </a>
        <Link to="/ai" className="flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90">
          <Bot className="w-4 h-4" /> Buat via AI
        </Link>
      </PageHeader>
      <div className="p-8">
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="quote-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari no penawaran / customer…" className="pl-9 rounded-sm bg-secondary" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="quote-status-filter" className="w-48 rounded-sm bg-secondary"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="request">Permintaan</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Terkirim</SelectItem>
              <SelectItem value="negotiating">Negosiasi</SelectItem>
              <SelectItem value="accepted">Disetujui</SelectItem>
              <SelectItem value="rejected">Ditolak</SelectItem>
              <SelectItem value="invoiced">Invoiced</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Input data-testid="quote-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 rounded-sm bg-secondary" />
            <span className="text-muted-foreground text-sm">s/d</span>
            <Input data-testid="quote-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 rounded-sm bg-secondary" />
          </div>
        </div>
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr className="text-left overline text-muted-foreground">
                <th className="px-4 py-3">No. Penawaran</th><th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                  <FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />Tidak ada penawaran.</td></tr>
              )}
              {filtered.map((q) => (
                <tr key={q.id} className="hover:bg-secondary/40" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{q.quote_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{q.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{q.customer_company}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">{idr(q.total)}</td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      data-testid={`download-excel-${q.id}`}
                      onClick={() => openDocument(`/quotations/${q.id}/xlsx`, `Penawaran-${q.quote_number}${q.revision ? `-Rev${String(q.revision).padStart(2, "0")}` : ""}.xlsx`).catch(() => toast.error("Gagal mengunduh Excel"))}
                      className="mr-4 text-xs inline-flex items-center gap-1 text-emerald-700 hover:underline"
                    >
                      <Download className="w-3 h-3" /> Excel
                    </button>
                    <Link to={`/quotations/${q.id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1" data-testid={`open-quote-${q.id}`}>
                      Detail <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
